import type { ProjectMetadataField } from "@/types/api";

interface RecordDefaults {
  tags?: string[];
  planId?: string | null;
  pinX?: number | null;
  pinY?: number | null;
  pinBearing?: number | null;
}

export function createDefaultValues(
  fields: ProjectMetadataField[],
  initialValues?: Record<string, unknown>,
  recordDefaults?: RecordDefaults,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {
    __tags: recordDefaults?.tags ?? [],
    __plan: recordDefaults?.planId ?? null,
    __pinX: recordDefaults?.pinX ?? null,
    __pinY: recordDefaults?.pinY ?? null,
    __pinBearing: recordDefaults?.pinBearing ?? null,
  };
  for (const field of fields) {
    const incoming = initialValues?.[field.id];
    if (incoming !== undefined && incoming !== null) {
      defaults[field.id] = incoming;
      continue;
    }
    switch (field.field_type) {
      case "boolean":
        defaults[field.id] = field.default_value ?? false;
        break;
      case "multi_select":
        defaults[field.id] = Array.isArray(field.default_value)
          ? field.default_value
          : [];
        break;
      case "number":
        defaults[field.id] = field.default_value ?? "";
        break;
      default:
        defaults[field.id] = field.default_value ?? "";
    }
  }
  return defaults;
}

export function getImageLayout(img: HTMLImageElement): {
  offsetX: number;
  offsetY: number;
  displayedWidth: number;
  displayedHeight: number;
} | null {
  if (!img.naturalWidth || !img.naturalHeight) return null;
  const rect = img.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;

  const imageAspect = img.naturalWidth / img.naturalHeight;
  const containerAspect = rect.width / rect.height;

  let displayedWidth: number;
  let displayedHeight: number;
  let offsetX = 0;
  let offsetY = 0;

  if (imageAspect > containerAspect) {
    displayedWidth = rect.width;
    displayedHeight = rect.width / imageAspect;
    offsetY = (rect.height - displayedHeight) / 2;
  } else {
    displayedHeight = rect.height;
    displayedWidth = rect.height * imageAspect;
    offsetX = (rect.width - displayedWidth) / 2;
  }

  return { offsetX, offsetY, displayedWidth, displayedHeight };
}

export function clientToImageCoords(
  clientX: number,
  clientY: number,
  img: HTMLImageElement,
): { x: number; y: number } | null {
  const layout = getImageLayout(img);
  if (!layout) return null;

  const rect = img.getBoundingClientRect();
  const relX = clientX - rect.left - layout.offsetX;
  const relY = clientY - rect.top - layout.offsetY;

  if (
    relX < 0 ||
    relX > layout.displayedWidth ||
    relY < 0 ||
    relY > layout.displayedHeight
  ) {
    return null;
  }

  return {
    x: Math.max(0, Math.min(1, relX / layout.displayedWidth)),
    y: Math.max(0, Math.min(1, relY / layout.displayedHeight)),
  };
}
